import dayjs from 'dayjs'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { DAY_TO_NUMBER } from '@/types/offer'
import { CHANNEL_LABEL_KEY, getOfferStatus, getSampleDateForDay } from '@/utils/offerHelpers'

import type { Ref } from 'vue'
import type { DistributionChannel, TargetAudience, TimeSlot } from '@/types/offer'
import type { Audience } from '@/types/types'

export interface UseOfferSummaryOptions {
  targetAudience: Ref<TargetAudience>
  selectedSegmentIds: Ref<number[]>
  allCustomerLists: Ref<Audience[]>
  scheduleStartDate: Ref<string | null>
  scheduleEndDate: Ref<string | null>
  timeSlots: Ref<TimeSlot[]>
  selectedChannels: Ref<DistributionChannel[]>
  offerStoreIds: Ref<number[]>
}

export const useOfferSummary = (options: UseOfferSummaryOptions) => {
  const { t, locale } = useI18n()

  const {
    targetAudience,
    selectedSegmentIds,
    allCustomerLists,
    scheduleStartDate,
    scheduleEndDate,
    timeSlots,
    selectedChannels,
    offerStoreIds
  } = options

  const offerAudienceSummary = computed(() => {
    if (targetAudience.value === 'all') {
      return t('pages.offer.create.summary.audienceAll')
    }
    if (targetAudience.value === 'loyal') {
      return t('pages.offer.create.summary.audienceLoyal')
    }
    if (targetAudience.value === 'segment' && selectedSegmentIds.value.length > 0) {
      const segments = allCustomerLists.value.filter((a) => a.id != null && selectedSegmentIds.value.includes(a.id))

      if (segments.length === 1) {
        return t('pages.offer.create.summary.audienceSegment', { name: segments[0].name })
      }

      if (segments.length > 1) {
        const names = segments.map((s) => s.name)
        const displayedNames = names.slice(0, 2).join(', ')
        const remaining = names.length - 2

        if (remaining > 0) {
          return t('pages.offer.create.summary.audienceSegmentsMultipleWithMore', {
            names: displayedNames,
            count: remaining
          })
        }

        return t('pages.offer.create.summary.audienceSegmentsMultiple', {
          names: displayedNames
        })
      }
    }
    return t('pages.offer.create.summary.audienceUndefined')
  })

  const offerScheduleSummary = computed(() => {
    const start = scheduleStartDate.value ? dayjs(scheduleStartDate.value).format('DD/MM/YYYY') : null
    const end = scheduleEndDate.value ? dayjs(scheduleEndDate.value).format('DD/MM/YYYY') : null

    let datePart = ''
    if (start && end) {
      datePart = t('pages.offer.list.table.period.range', { start, end })
    } else if (start && !end) {
      datePart = t('pages.offer.list.table.period.from', { start })
    } else if (!start && end) {
      datePart = t('pages.offer.list.table.period.until', { end })
    }

    const slotsWithContent = timeSlots.value.filter((slot) => slot.days.length > 0 || slot.startTime || slot.endTime)

    if (slotsWithContent.length === 0) {
      return datePart || t('pages.offer.list.table.period.undefined')
    }

    const formatter = new Intl.DateTimeFormat(locale.value || 'en', { weekday: 'long' })
    const slotsLabel = slotsWithContent
      .map((slot) => {
        let daysLabel: string
        if (slot.days.length > 0) {
          const sortedDays = [...slot.days].sort((a, b) => DAY_TO_NUMBER[a] - DAY_TO_NUMBER[b])
          const isContinuousRange =
            sortedDays.length > 1 &&
            sortedDays.every((d, i, arr) => {
              if (i === 0) return true
              return DAY_TO_NUMBER[d] === DAY_TO_NUMBER[arr[i - 1]] + 1
            })

          if (isContinuousRange) {
            const startLabel = formatter.format(getSampleDateForDay(sortedDays[0]))
            const endLabel = formatter.format(getSampleDateForDay(sortedDays[sortedDays.length - 1]))
            daysLabel = t('pages.offer.reward.timeSlotDaysRange', { start: startLabel, end: endLabel })
          } else {
            daysLabel = sortedDays.map((d) => formatter.format(getSampleDateForDay(d))).join(', ')
          }
        } else {
          daysLabel = t('pages.offer.detail.availability.daysUndefined')
        }

        const timeLabel =
          slot.startTime && slot.endTime
            ? `${slot.startTime} – ${slot.endTime}`
            : t('pages.offer.detail.availability.timesUndefined')

        return `${daysLabel} ${timeLabel}`
      })
      .join(' / ')

    const base = datePart || t('pages.offer.list.table.period.undefined')

    return `${base} — ${slotsLabel}`
  })

  const offerStatus = computed(() => getOfferStatus(scheduleStartDate.value, scheduleEndDate.value))

  const offerChannelsSummary = computed(() => {
    if (selectedChannels.value.length === 0) {
      return t('pages.offer.detail.channels.none')
    }
    return selectedChannels.value.map((c) => t(CHANNEL_LABEL_KEY[c])).join(', ')
  })

  const offerStoresSummary = computed(() => {
    if (offerStoreIds.value.length === 0) {
      return t('global.multiselect.stores.allSelectedText')
    }

    return t('global.multiselect.stores.storesSelected', {
      count: offerStoreIds.value.length
    })
  })

  return {
    offerAudienceSummary,
    offerScheduleSummary,
    offerStatus,
    offerChannelsSummary,
    offerStoresSummary
  }
}
